import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MessageBox, {MessageType} from "../components/MessageBox";
import {Tour} from "../../models/tour";
import {TourMembership} from "../../models/membership";
import {QUERY_PARAMS} from "../../constants/queryParams";
import {useSearchParams} from "react-router-dom";
import {getAllTours, getAllToursByTourCodeOrDestination} from "../../api/tourApi";
import FoundTour from "../components/FoundTour";
import {EmergencyContact} from "../../models/emergencyContact";
import DialogCloseButton from "../buttons/DialogCloseButton";
import {isDateInPast} from "../../utils/formatDateTime";

type Props = {
    accountEmergencyContacts: EmergencyContact[];
    tourMemberships: TourMembership[];
    onReloadTours: () => void;
    onClose: () => void;
};

export default function TourSearchDialog({
                                             accountEmergencyContacts,
                                             tourMemberships,
                                             onReloadTours,
                                             onClose
                                         }: Props) {

    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [message, setMessage] = useState<MessageType>(null);
    const [inputString, setInputString] = useState<string>("");
    const [tours, setTours] = useState<Tour[]>([]);
    const [hideHistoricalTours, setHideHistoricalTours] = useState<boolean>(true);
    const [hideJoinedTours, setHideJoinedTours] = useState<boolean>(true);

    /**
     * API Suche
     */
    const handleSearch = async (value: string) => {
        setMessage(null);

        if (!value) {
            const all = await getAllTours();
            setTours(all);
            return;
        }

        try {
            const foundTours = await getAllToursByTourCodeOrDestination(value);
            setTours(foundTours);
        } catch {
            setMessage({type: "error", text: t("errors.accountNotFound")});
        }
    };

    function handleClose() {
        setInputString("");
        setMessage(null);
        setSearchParams({});
        onClose();
    }

    function hideTour(tour: Tour): boolean {
        const hideHistorical = hideHistoricalTours && isDateInPast(tour.validTo);

        const joinedTourIds = new Set(tourMemberships.map(m => m.tour.id));
        const hideJoined = hideJoinedTours && joinedTourIds.has(tour.id);

        return hideHistorical || hideJoined;
    }

    /**
     * Debounced Suche (300ms)
     */
    useEffect(() => {
        const timeout = setTimeout(() => {
            handleSearch(inputString);

            // URL synchronisieren (nur wenn nötig)
            const currentUrlValue = searchParams.get(QUERY_PARAMS.TOUR) || "";
            if (inputString !== currentUrlValue) {
                if (inputString) {
                    setSearchParams({[QUERY_PARAMS.TOUR]: inputString});
                } else {
                    setSearchParams({});
                }
            }

        }, 300);

        return () => clearTimeout(timeout);
    }, [inputString]);

    /**
     * URL → Input synchronisieren
     * (z.B. wenn Seite direkt mit ?tour=ABC geladen wird)
     */
    useEffect(() => {
        const tourFromUrl = searchParams.get(QUERY_PARAMS.TOUR) || "";

        if (tourFromUrl !== inputString) {
            setInputString(tourFromUrl);
        }
    }, [searchParams]);

    return (
        <div className="dialog-overlay">
            <div className="dialog">
                <h2>{t("tours.dialog.searchTour")}</h2>

                <DialogCloseButton
                    onClose={handleClose}
                />

                <form className="form-container" onSubmit={(e) => e.preventDefault()}>
                    <MessageBox message={message}/>

                    <input
                        type="text"
                        name="tourCode"
                        value={inputString}
                        placeholder={t("tours.fields.tourInput")}
                        onChange={(e) => setInputString(e.target.value)}
                        className="form-input"
                        required
                    />

                    <div className="toggle-row">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={hideHistoricalTours}
                                onChange={() => setHideHistoricalTours(!hideHistoricalTours)}
                            />
                            <span className="slider"></span>
                            <span className="switch-label">{t("tours.fields.hideHistoricalTours")}</span>
                        </label>

                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={hideJoinedTours}
                                onChange={() => setHideJoinedTours(!hideJoinedTours)}
                            />
                            <span className="slider"></span>
                            <span className="switch-label">{t("tours.fields.hideJoinedTours")}</span>
                        </label>
                    </div>

                </form>

                <div className="scrollbar-area">
                    {tours.filter(t => !hideTour(t)).map((t, index, filteredTours) => (
                        <div>
                            <FoundTour
                                key={t.id}
                                tour={t}
                                accountEmergencyContacts={accountEmergencyContacts}
                                tourMemberships={tourMemberships}
                                onReloadTours={onReloadTours}
                                onCloseSearch={handleClose}
                            />

                            {index < filteredTours.length - 1 && (
                                <div className="member-divider"></div>
                            )}
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
}
